import { publicAssetUrl } from "../config";

/**
 * Live championship cheers shared with the iOS match room.
 * Sounds are scheduled against the server clock (`timeSync`) so browsers and
 * phones start together. Emoji kinds are visual only.
 */

export const CHEER_SOUNDS = [
  { kind: "clap", emoji: "👏", labelKey: "cmCheerClap", label: "Clap" },
  { kind: "cowbell", emoji: "🔔", labelKey: "cmCheerCowbell", label: "Bell" },
  { kind: "fanfare", emoji: "🎺", labelKey: "cmCheerFanfare", label: "Fanfare" },
];

export const CHEER_EMOJIS = [
  { kind: "emoji_clap", emoji: "👏", labelKey: "cmCheerClap", label: "Clap" },
  { kind: "emoji_fire", emoji: "🔥", labelKey: "cmCheerFire", label: "Fire" },
  { kind: "emoji_party", emoji: "🎉", labelKey: "cmCheerParty", label: "Party" },
  { kind: "emoji_flex", emoji: "💪", labelKey: "cmCheerFlex", label: "Flex" },
  { kind: "emoji_wow", emoji: "😮", labelKey: "cmCheerWow", label: "Wow" },
  { kind: "emoji_laugh", emoji: "😂", labelKey: "cmCheerLaugh", label: "Laugh" },
];

/** Same synchronized start as the other match sounds. */
export const SYNC_SOUNDS = [];

const SOUND_KINDS = new Set(CHEER_SOUNDS.map((item) => item.kind));
const PLAYABLE_KINDS = new Set([
  ...SOUND_KINDS,
  ...SYNC_SOUNDS.map((item) => item.kind),
]);
const ALL_KINDS = new Set([
  ...CHEER_SOUNDS.map((item) => item.kind),
  ...CHEER_EMOJIS.map((item) => item.kind),
]);

let mutedKinds = new Set();
let soundsMuted = false;
/** True while any match camera is recording. Sounds stay silent so the mic stays clean. */
let videoRecording = false;

function soundKindKey(kind) {
  const key = String(kind);
  return key === "supporters" ? "fanfare" : key;
}

export function isCheerMuted(kind) {
  const key = String(kind);
  if (soundsMuted && SOUND_KINDS.has(soundKindKey(key))) return true;
  return mutedKinds.has(key);
}

export function mutedCheerKinds() {
  return new Set(mutedKinds);
}

export function cheerSoundsMuted() {
  return soundsMuted;
}

export function cheerVideoRecording() {
  return videoRecording;
}

/** Silence cheer audio on this device for the whole time a camera is recording. */
export function setCheerVideoRecording(on) {
  videoRecording = Boolean(on);
  if (videoRecording) stopMatchSounds();
}

/** Local mute for this phone only. Cleared when the next point is scored. */
export function toggleCheerMuted(kind) {
  const key = String(kind);
  if (mutedKinds.has(key)) mutedKinds.delete(key);
  else mutedKinds.add(key);
  mutedKinds = new Set(mutedKinds);
  return mutedKinds.has(key);
}

/** Mute every cheer sound on this phone until the next point. */
export function toggleCheerSoundsMuted() {
  soundsMuted = !soundsMuted;
  if (soundsMuted) {
    for (const item of CHEER_SOUNDS) fadeCheerKind(item.kind);
  } else {
    for (const item of CHEER_SOUNDS) restoreCheerKind(item.kind);
  }
  return soundsMuted;
}

export function clearCheerMutes() {
  soundsMuted = false;
  mutedKinds = new Set();
}

let serverOffsetMs = 0;
let audioCtx = null;
const buffers = new Map();
let preloadPromise = null;
let syncSource = null;
const liveSources = new Set();
const sourcesByKind = new Map();
const gainBySource = new WeakMap();
const fadeTimerBySource = new Map();
let playGeneration = 0;

/** Exponential fade so mute settles into silence instead of cutting the sample. */
const MUTE_FADE_SEC = 1.15;

export function estimateServerOffset(clientSendMs, serverTimeMs, clientReceiveMs) {
  const rtt = clientReceiveMs - clientSendMs;
  return serverTimeMs - (clientSendMs + rtt / 2);
}

export function setCheerServerOffset(offsetMs) {
  if (Number.isFinite(offsetMs)) serverOffsetMs = offsetMs;
}

export function cheerServerNow(localNow = Date.now()) {
  return localNow + serverOffsetMs;
}

/** Milliseconds until `playAtMs`. Negative means this client is late. */
export function cheerDelayMs(playAtMs, serverNowMs = cheerServerNow()) {
  const target = Number(playAtMs);
  if (!Number.isFinite(target)) return 0;
  return target - serverNowMs;
}

export function cheerEmoji(kind) {
  const row = [...CHEER_SOUNDS, ...CHEER_EMOJIS].find((item) => item.kind === kind);
  return row ? row.emoji : "";
}

export function isCheerKind(kind) {
  return ALL_KINDS.has(kind);
}

/**
 * OVH sets `window.__PUBLIC_URL__` to "" while the dev server still serves
 * `public/` under the baked homepage path (`/smarttt-frontend-prod`). The
 * same order as `index.html`: baked path first, then the runtime URL.
 */
export function cheerAssetUrl(kind, options = {}) {
  const fileKind = kind === "fanfare" ? "supporters" : kind;
  const path = `/cheers/cheer_${fileKind}.wav`;
  const runtime = Object.prototype.hasOwnProperty.call(options, "runtimePublicUrl")
    ? options.runtimePublicUrl
    : typeof window !== "undefined"
      ? window.__PUBLIC_URL__
      : "";
  const baked = Object.prototype.hasOwnProperty.call(options, "bakedPublicUrl")
    ? options.bakedPublicUrl
    : typeof process !== "undefined" && process.env
      ? process.env.PUBLIC_URL
      : "";
  const base = String(baked || runtime || "").replace(/\/$/, "");
  if (!base) return publicAssetUrl(path);
  return `${base}${path}`;
}

function assetUrl(kind) {
  return cheerAssetUrl(kind);
}

function context() {
  if (typeof window === "undefined") return null;
  if (!audioCtx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    audioCtx = new AC();
  }
  return audioCtx;
}

export async function unlockCheerAudio() {
  const ctx = context();
  if (ctx && ctx.state === "suspended") {
    try {
      await ctx.resume();
    } catch {
      /* a later gesture can resume */
    }
  }
  await preloadCheerSounds();
}

export function preloadCheerSounds() {
  if (preloadPromise) return preloadPromise;
  const ctx = context();
  if (!ctx) {
    preloadPromise = Promise.resolve();
    return preloadPromise;
  }
  preloadPromise = Promise.all(
    [...PLAYABLE_KINDS].map(async (kind) => {
      if (buffers.has(kind)) return;
      const res = await fetch(assetUrl(kind));
      const type = String(res.headers.get("content-type") || "");
      if (!res.ok || !type.startsWith("audio/")) return;
      const raw = await res.arrayBuffer();
      const decoded = await ctx.decodeAudioData(raw.slice(0));
      buffers.set(kind, decoded);
    })
  ).catch(() => {
    preloadPromise = null;
  });
  return preloadPromise;
}

export function syncCheerClock(socket) {
  if (!socket || typeof socket.emit !== "function") return;
  const round = (left, best) => {
    if (left <= 0) {
      if (best) setCheerServerOffset(best.offset);
      return;
    }
    const clientSend = Date.now();
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      round(left - 1, best);
    }, 1500);
    socket.emit("timeSync", { client_time: clientSend }, (res) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      const clientReceive = Date.now();
      const serverTime = Number(res?.server_time);
      if (!Number.isFinite(serverTime)) {
        round(left - 1, best);
        return;
      }
      const rtt = clientReceive - clientSend;
      const offset = estimateServerOffset(clientSend, serverTime, clientReceive);
      const next = !best || rtt < best.rtt ? { rtt, offset } : best;
      round(left - 1, next);
    });
  };
  round(3, null);
}

/** Schedule a sound. `delayMs` is milliseconds until the shared `play_at`. */
function startBuffer(ctx, buffer, delayMs, kind) {
  const delaySec = Number(delayMs) / 1000;
  if (delaySec < -1.5) return null;
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(1, ctx.currentTime);
  source.connect(gain);
  gain.connect(ctx.destination);
  gainBySource.set(source, gain);
  const bucket = sourcesByKind.get(kind) || new Set();
  sourcesByKind.set(kind, bucket);
  const forget = () => {
    liveSources.delete(source);
    bucket.delete(source);
  };
  source.onended = forget;
  liveSources.add(source);
  bucket.add(source);
  if (delaySec > 0.02) {
    source.start(ctx.currentTime + delaySec);
    return source;
  }
  const late = Math.min(
    Math.abs(Math.min(delaySec, 0)),
    Math.max(0, buffer.duration - 0.05)
  );
  source.start(0, late);
  return source;
}

function cancelFade(source) {
  const timer = fadeTimerBySource.get(source);
  if (!timer) return;
  clearTimeout(timer);
  fadeTimerBySource.delete(source);
}

function hardStop(source) {
  cancelFade(source);
  try {
    source.stop();
  } catch {
    /* already stopped */
  }
  liveSources.delete(source);
  for (const bucket of sourcesByKind.values()) bucket.delete(source);
  if (syncSource === source) syncSource = null;
}

function fadeSourceOut(source) {
  const gain = gainBySource.get(source);
  const ctx = source.context;
  if (!gain || !ctx) {
    hardStop(source);
    return;
  }
  const now = ctx.currentTime;
  const param = gain.gain;
  try {
    param.cancelScheduledValues(now);
    const from = Math.max(param.value, 0.0008);
    param.setValueAtTime(from, now);
    param.exponentialRampToValueAtTime(0.0008, now + MUTE_FADE_SEC);
  } catch {
    hardStop(source);
    return;
  }
  cancelFade(source);
  const timer = setTimeout(() => {
    fadeTimerBySource.delete(source);
    const level = gainBySource.get(source)?.gain?.value ?? 0;
    if (level <= 0.02) hardStop(source);
  }, MUTE_FADE_SEC * 1000 + 40);
  fadeTimerBySource.set(source, timer);
}

function restoreSource(source) {
  cancelFade(source);
  const gain = gainBySource.get(source);
  const ctx = source.context;
  if (!gain || !ctx) return;
  const now = ctx.currentTime;
  const param = gain.gain;
  try {
    param.cancelScheduledValues(now);
    const from = Math.max(param.value, 0.0008);
    param.setValueAtTime(from, now);
    param.exponentialRampToValueAtTime(1, now + 0.22);
  } catch {
    /* node already closed */
  }
}

function fadeCheerKind(kind) {
  const bucket = sourcesByKind.get(String(kind));
  if (!bucket) return;
  for (const source of bucket) fadeSourceOut(source);
}

function restoreCheerKind(kind) {
  const bucket = sourcesByKind.get(String(kind));
  if (!bucket) return;
  for (const source of bucket) restoreSource(source);
}

export function stopCheerKind(kind) {
  const bucket = sourcesByKind.get(String(kind));
  if (!bucket) return;
  for (const source of [...bucket]) hardStop(source);
  if (kind === "fanfare" || kind === "supporters") syncSource = null;
}

export function stopSyncSound() {
  if (!syncSource) return;
  hardStop(syncSource);
}

/** Stop every cheer still playing on this device. Used when leaving a match. */
export function stopMatchSounds() {
  playGeneration += 1;
  stopSyncSound();
  for (const source of [...liveSources]) hardStop(source);
  liveSources.clear();
  sourcesByKind.clear();
}

export function playCheerSound(kind, delayMs) {
  const playKind = kind === "supporters" ? "fanfare" : kind;
  if (videoRecording || !PLAYABLE_KINDS.has(playKind) || isCheerMuted(playKind)) return;
  const ctx = context();
  if (!ctx) return;
  const generation = playGeneration;
  const play = () => {
    if (generation !== playGeneration || videoRecording || isCheerMuted(playKind)) return;
    const buffer = buffers.get(playKind);
    if (!buffer) return;
    if (playKind === "fanfare") stopCheerKind(playKind);
    const source = startBuffer(ctx, buffer, delayMs, playKind);
    if (playKind === "fanfare") syncSource = source;
  };
  const ready = buffers.has(playKind) ? Promise.resolve() : preloadCheerSounds();
  void ready.then(() => {
    if (generation !== playGeneration) return;
    if (ctx.state === "suspended") {
      void ctx.resume().then(play);
      return;
    }
    play();
  });
}
